import { createRoot } from 'react-dom/client'
import { createBrowserRouter, RouterProvider } from 'react-router-dom'
import { TaskProvider } from './utils/tasks/TaskContext'
import { Auth0Provider } from '@/utils/auth/auth0'
import { QueryClientProvider, QueryClient } from '@tanstack/react-query'
import * as Sentry from '@sentry/react'

import { UserProvider } from './utils/auth/UserProvider'
import { routes } from './routes'

// Initialize Sentry only if not in test mode and DSN is provided
if (import.meta.env.MODE !== 'test' && import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Additional Replay configuration goes in here, for example:
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    // Performance Monitoring
    tracesSampleRate: parseFloat(
      import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || '0.1'
    ), // Capture 10% of transactions
    // Session Replay
    replaysSessionSampleRate: parseFloat(
      import.meta.env.VITE_SENTRY_REPLAYS_SAMPLE_RATE || '0.1'
    ), // This sets the sample rate at 10%. You may want this to be lower
    replaysOnErrorSampleRate: parseFloat(
      import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE || '1.0'
    ), // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    environment:
      import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE,
    release: import.meta.env.VITE_SENTRY_RELEASE || 'local-dev',
  })
  console.log(
    'Sentry initialized for environment:',
    import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE
  )
} else {
  console.log('Sentry initialization skipped for mode:', import.meta.env.MODE)
}

const domain = import.meta.env.VITE_AUTH0_DOMAIN
const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID
const audience = import.meta.env.VITE_AUTH0_AUDIENCE

const queryClient = new QueryClient()

const router = createBrowserRouter(routes)

document.addEventListener('DOMContentLoaded', () => {
  createRoot(document.getElementById('app') as HTMLElement).render(
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      audience={audience}
      redirectUri={window.location.origin}
      cacheLocation={'localstorage'}
    >
      <QueryClientProvider client={queryClient}>
        <TaskProvider>
          <UserProvider>
            <RouterProvider router={router} />
          </UserProvider>
        </TaskProvider>
      </QueryClientProvider>
    </Auth0Provider>
  )
})
